
import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DatabaseZap, ArrowRight, Plug, Upload } from 'lucide-react';

interface PortfolioData {
  totalValue: number;
  dailyChange: number;
  assetMix: {
    type: string;
    percentage: number;
    color: string;
  }[];
}

interface PortfolioSnapshotProps {
  data?: PortfolioData;
  onUploadDataClick?: () => void;
  onConnectBrokerClick?: () => void;
}

export const PortfolioSnapshot: React.FC<PortfolioSnapshotProps> = ({ data, onUploadDataClick, onConnectBrokerClick }) => {
  if (!data) {
    return (
      <Card className="rounded-lg border bg-gradient-to-br from-gray-50 to-white p-6 space-y-4">
        <div className="flex flex-col items-center justify-center text-center space-y-4">
          <div className="rounded-full bg-blue-50 p-3">
            <DatabaseZap className="h-6 w-6 text-blue-500" />
          </div>
          
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">Connect Your Broker or Data</h3>
            <p className="text-sm text-gray-500 max-w-[280px]">
              Link your brokerage account or upload your own data to view your portfolio value, daily performance, and asset allocation.
            </p>
          </div>

          <div className="w-full max-w-xs space-y-3">
            {/* Preview blocks with shimmer effect */}
            <div className="space-y-2 text-left">
              <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
              <div className="h-6 w-32 bg-gray-100 rounded animate-pulse" />
            </div>
            
            <div className="flex justify-between py-2 border-t border-dashed border-gray-200">
              <div className="space-y-1">
                <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-20 bg-gray-100 rounded animate-pulse" />
              </div>
              <div className="space-y-1">
                <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-20 bg-gray-100 rounded animate-pulse" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
                <div className="w-2/5 h-full bg-blue-100 animate-pulse" />
                <div className="w-1/4 h-full bg-green-100 animate-pulse" />
                <div className="w-1/5 h-full bg-yellow-100 animate-pulse" />
                <div className="w-1/6 h-full bg-purple-100 animate-pulse" />
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              variant="outline"
              className="gap-2 mt-4 border-blue-100 hover:border-blue-200 hover:bg-blue-50/50"
              onClick={onConnectBrokerClick}
            >
              <Plug className="h-4 w-4" />
              <span>Connect Broker</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline"
              className="gap-2 mt-4 border-blue-100 hover:border-blue-200 hover:bg-blue-50/50"
              onClick={onUploadDataClick}
            >
              <Upload className="h-4 w-4" />
              <span>Connect Data</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="rounded-lg border bg-white p-6 space-y-4">
      <h3 className="font-semibold text-lg">Portfolio Snapshot</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-gray-500">Total Value</p>
          <p className="text-lg font-semibold">${data.totalValue.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Daily Change</p>
          <p className={`text-lg font-semibold ${data.dailyChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {data.dailyChange >= 0 ? '+' : ''}{data.dailyChange}%
          </p>
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium">Asset Mix</p>
        <div className="flex h-2 rounded-full overflow-hidden">
          {data.assetMix.map((asset, index) => (
            <div
              key={index}
              className={asset.color}
              style={{ width: `${asset.percentage}%` }}
              title={`${asset.type} (${asset.percentage}%)`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
